<template>
  <div class="responsive-image-background" :style="wrapperStyles">
    <transition name="fade">
      <picture v-if="isImageSrsetLoaded" class="responsive-image" :class="imageClasses">
        <source :srcset="largewp" :media="`(min-width: ${breakpoints.large})`" type="image/webp" />
        <source
          :srcset="mediumwp"
          :media="`(min-width: ${breakpoints.medium})`"
          type="image/webp"
        />
        <source :srcset="smallwp" type="image/webp" />

        <source :srcset="large" :media="`(min-width: ${breakpoints.large})`" />
        <source :srcset="medium" :media="`(min-width: ${breakpoints.medium})`" />
        <source :srcset="small" />

        <img
          class="responsive-image__image"
          :src="defaultSrc"
          alt=""
          v-bind="$attrs"
          :loading="imageLoadingType"
        /> </picture
    ></transition>
  </div>
</template>

<script>
import { log } from '@/utils/loger';

const getSrset = (normal, hd) => {
  if (normal && hd) {
    return `${normal}, ${hd} 2x`;
  }

  return null;
};

export default {
  name: 'ResponsiveImage',
  props: {
    name: {
      type: String,
      required: true
    },
    breakpoints: {
      type: Object,
      default: () => ({ large: '1440px', medium: '960px' })
    },
    fitObject: {
      type: Boolean,
      default: false
    },
    imageHeightToWidthRatio: {
      type: Number,
      required: true
    },
    imageLazy: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      small: null,
      medium: null,
      large: null,
      smallwp: null,
      mediumwp: null,
      largewp: null,
      defaultSrc: null,
      isImageSrsetLoaded: false
    };
  },
  computed: {
    imageClasses() {
      return {
        'responsive-image--fit-object': this.fitObject
      };
    },
    wrapperStyles() {
      return {
        '--image-height-to-width-ratio': this.imageHeightToWidthRatio
      };
    },
    imageLoadingType() {
      return this.imageLazy ? 'lazy' : 'auto';
    }
  },
  watch: {
    name() {
      this.setImage();
    }
  },
  async created() {
    await this.setImage();
    this.isImageSrsetLoaded = true;
  },
  methods: {
    async getImage(size, extension) {
      try {
        const imgModule = await import(
          `@/assets/images/responsive/${this.name}/${size}.${extension}`
        );

        return imgModule.default;
      } catch (err) {
        log.warn(err);
      }

      return null;
    },
    async setImage() {
      const smallWebp = await this.getImage('s', 'webp');
      const smallHdWebp = await this.getImage('s-hd', 'webp');
      this.smallwp = getSrset(smallWebp, smallHdWebp);

      const mediumWebp = await this.getImage('m', 'webp');
      const mediumHdWebp = await this.getImage('m-hd', 'webp');
      this.mediumwp = getSrset(mediumWebp, mediumHdWebp);

      const largeWebp = await this.getImage('l', 'webp');
      const largeHdWebp = await this.getImage('l-hd', 'webp');
      this.largewp = getSrset(largeWebp, largeHdWebp);

      const small = await this.getImage('s', 'jpg');
      const smallHd = await this.getImage('s-hd', 'jpg');
      this.small = getSrset(small, smallHd);

      const medium = await this.getImage('m', 'jpg');
      const mediumHd = await this.getImage('m-hd', 'jpg');
      this.medium = getSrset(medium, mediumHd);

      const large = await this.getImage('l', 'jpg');
      const largeHd = await this.getImage('l-hd', 'jpg');
      this.large = getSrset(large, largeHd);

      this.defaultSrc = large;
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

@keyframes image-background {
  0% {
    background-color: var(--u-color-grey-50);
  }
  100% {
    background-color: var(--u-color-grey-100);
  }
}

.responsive-image-background {
  position: relative;
  width: 100%;
  animation: image-background 0.5s linear infinite alternate;

  /* Dynamically calculate height depending on width value */
  height: 0;
  padding-bottom: calc(100% * var(--image-height-to-width-ratio));
}

.responsive-image {
  width: 100%;
  position: absolute;
  top: 0;
}

.responsive-image--fit-object {
  display: block;
  height: 100%;
}

.responsive-image--fit-object .responsive-image__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
}
</style>
